// src/Login.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react'
import { setToken } from '../utils/auth'
import LoadingScreen from '../components/LoadingScreen'

const API_BASE = import.meta.env.VITE_API_BASE;

// Small loading spinner for buttons that matches the LoadingScreen style
const LoadingSpinner = ({ size = 5, className = '' }) => (
  <div className={`w-${size} h-${size} border-2 border-t-transparent border-white rounded-full animate-spin mr-2 ${className}`}></div>
);

const GoogleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4"/>
    <path d="M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.24 19.252C9.07376 19.252 6.39287 17.1399 5.44288 14.3003H1.45801V17.3912C3.43223 21.4434 7.54226 24.0008 12.24 24.0008Z" fill="#34A853"/>
    <path d="M5.44277 14.3003C5.21387 13.5681 5.08188 12.7862 5.08188 12.0004C5.08188 11.2146 5.21387 10.4327 5.44277 9.70051V6.60962H1.45791C0.530724 8.24284 0 10.0682 0 12.0004C0 13.9326 0.530724 15.7579 1.45791 17.3912L5.44277 14.3003Z" fill="#FBBC05"/>
    <path d="M12.24 4.74966C14.0291 4.74966 15.6265 5.36715 16.8582 6.54066L20.2717 3.12721C18.2003 1.18941 15.4708 0 12.24 0C7.54226 0 3.43223 2.55737 1.45801 6.60962L5.44288 9.70051C6.39287 6.86095 9.07376 4.74966 12.24 4.74966Z" fill="#EA4335"/>
  </svg>
);

export default function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();
  const googleInitializedRef = useRef(false);
  const scriptLoadAttemptedRef = useRef(false);
  const googleButtonContainerRef = useRef(null);

  useEffect(() => {
    // Skip if script loading was already attempted
    if (scriptLoadAttemptedRef.current) return;
    scriptLoadAttemptedRef.current = true;
    
    // Clear any existing Google state and script on page load
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
    
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create fresh script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (googleInitializedRef.current) return;
      console.log('Google script loaded successfully');
      
      if (window.google) {
        try {
          // Clear any existing configurations
          window.google.accounts.id.cancel();
          
          // Wait a brief moment before initializing
          setTimeout(() => {
            window.google.accounts.id.initialize({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              callback: handleGoogleResponse,
              auto_select: false,
              cancel_on_tap_outside: true,
              prompt_parent_id: 'google_btn_container',
              context: 'signin',
              itp_support: true,
              use_fedcm_for_prompt: true,
              state_cookie_domain: window.location.hostname,
              allowed_parent_origin: window.location.origin
            });
            
            googleInitializedRef.current = true;
            setGoogleScriptLoaded(true);
            console.log('Google authentication initialized');
          }, 100);
        } catch (error) {
          console.error('Error initializing Google authentication:', error);
        }
      } else {
        console.error('Google API not available after script load');
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google authentication script');
    };
    document.body.appendChild(script);

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const handleGoogleClick = () => {
    console.log('Google button clicked');
    if (!window.google?.accounts?.id) {
      console.error('Google Sign-In not initialized');
      setGeneralError('Google Sign-In not available. Please try again.');
      return;
    }

    try {
      console.log('Attempting Google Sign-In');
      // Cancel any existing prompt first
      window.google.accounts.id.cancel();

      // Configure One Tap and standard sign-in
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin'
      });
      
      // Create a hidden button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.display = 'none';
      document.body.appendChild(buttonContainer);

      // Render the button (it will be hidden)
      window.google.accounts.id.renderButton(
        buttonContainer,
        { type: 'standard' }
      );

      // Function to try the fallback method
      const tryFallbackMethod = () => {
        console.log('Executing fallback method');
        const button = buttonContainer.querySelector('div[role="button"]');
        if (button) {
          button.click();
        }
      };

      let oneTapTriggered = false;

      // Set a timeout to try fallback if One Tap doesn't respond
      const timeoutId = setTimeout(() => {
        if (!oneTapTriggered) {
          console.log('One Tap timeout - no response received');
          tryFallbackMethod();
        }
      }, 1000);

      // Try One Tap first
      console.log('Trying One Tap');
      window.google.accounts.id.prompt((notification) => {
        oneTapTriggered = true;
        clearTimeout(timeoutId);
        
        console.log('One Tap callback received:', notification);
        
        if (!notification) {
          console.log('No notification, trying fallback');
          tryFallbackMethod();
          return;
        }

        // Only try fallback for specific cases
        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          console.log('One Tap not displayed. Reason:', reason);
          tryFallbackMethod();
        } else if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason();
          console.log('One Tap skipped. Reason:', reason);
          if (reason === 'user_cancel') {
            // User explicitly cancelled, don't show fallback
            console.log('User cancelled One Tap');
          } else {
            tryFallbackMethod();
          }
        } else if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          console.log('One Tap dismissed. Reason:', reason);
          if (reason === 'credential_returned') {
            // Successful sign-in, don't do anything
            console.log('Successful sign-in via One Tap');
          } else if (reason === 'flow_restarted') {
            // Flow is being restarted, don't do anything
            console.log('One Tap flow restarted');
          } else {
            tryFallbackMethod();
          }
        }
        // Don't handle unknown states - let One Tap continue
      });

      // Cleanup function
      const cleanup = () => {
        if (document.body.contains(buttonContainer)) {
          document.body.removeChild(buttonContainer);
        }
      };

      // Clean up after 5 seconds
      setTimeout(cleanup, 5000);

    } catch (error) {
      console.error('Error in Google Sign-In:', error);
      setGeneralError('Connection error. Please try again.');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Only clear the error for the field being changed
    setErrors(prev => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading || !validateForm()) return;
    setLoading(true);
    setGeneralError('');

    try {
      // First check if there's an ongoing registration
      const checkRes = await fetch(`${API_BASE}/signup/check?email=${encodeURIComponent(formData.email)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const checkData = await checkRes.json().catch(() => ({}));

      if (checkRes.ok && checkData.registrationToken) {
        // There's an ongoing registration, redirect to it
        sessionStorage.setItem('registrationToken', checkData.registrationToken);
        navigate(`/signup/${checkData.registrationToken}`, { replace: true });
        return;
      }

      // No ongoing registration, proceed with login
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          sentFrom: 'login'
        })
      });

      const data = await res.json();

      if (res.ok) {
        if (data.phoneCountry && data.phone && data.regToken) {
          // Store phone information for SMS verification
          const phoneVerificationData = {
            PhoneCountry: data.phoneCountry,
            Phone: data.phone,
            regToken: data.regToken
          };
          
          sessionStorage.setItem('phoneVerificationData', JSON.stringify(phoneVerificationData));
          navigate('/sms-verification');
          return;
        }
        
        // Check for registration token first
        if (data.regToken) {
          sessionStorage.setItem('registrationToken', data.regToken);
          navigate(`/signup/${data.regToken}`, { replace: true });
          return;
        }

        // Handle JWT token cases
        if (data.jwtToken || data.token) {
          // Store the JWT token
          setToken(data.jwtToken || data.token);
          navigate('/home', { replace: true });
        } else {
          setGeneralError('Invalid response from server');
        }
      } else if (res.status === 400) {
        setErrors(prev => ({
          ...prev,
          email: 'Invalid email or password'
        }));
      } else if (res.status === 401) {
        setGeneralError('Incorrect email or password');
      } else {
        setGeneralError('An unexpected error occurred');
      }
    } catch (err) {
      setGeneralError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have an ongoing registration
    const regToken = sessionStorage.getItem('registrationToken');
    if (regToken) {
      navigate(`/signup/${regToken}`);
      return;
    }
    
    // Check if we have a saved invite token
    const inviteRegToken = localStorage.getItem('inviteRegToken');
    if (inviteRegToken) {
      navigate(`/signup/${inviteRegToken}/invite?from=email&invite=true`);
      return;
    }
  }, [navigate]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) {
      setGeneralError('Google login failed. Please try again.')
      return
    }

    setGoogleLoading(true)
    setGeneralError('')
    
    try {
      const res = await fetch(`${API_BASE}/auth/google?sentFrom=login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          credential: response.credential 
        })
      })
      
      const data = await res.json()
      
      if (res.ok) {
        // Check for phone verification data with exact property names
        if (data.phoneCountry && data.phone && data.regToken) {
          // Store phone information for SMS verification
          const phoneVerificationData = {
            PhoneCountry: data.phoneCountry,
            Phone: data.phone,
            regToken: data.regToken
          };
          
          sessionStorage.setItem('phoneVerificationData', JSON.stringify(phoneVerificationData));
          navigate('/sms-verification');
          return;
        }

        // Check for registration token first
        if (data.regToken) {
          sessionStorage.setItem('registrationToken', data.regToken);
          navigate(`/signup/${data.regToken}`, { replace: true });
          return;
        }
        
        if (data.jwtToken) {
          // Store the JWT token and navigate to home
          setToken(data.jwtToken);
          navigate('/home', { replace: true });
        } else if (data.token) {
          // Backward compatibility for old token field
          setToken(data.token);
          navigate('/home', { replace: true });
        } else if (data.redirectUrl) {
          // Extract token from redirectUrl
          const registrationToken = data.redirectUrl.split('/').pop();
          
          // Save token for future use
          sessionStorage.setItem('registrationToken', registrationToken);
          
          // Check if we can determine if phone is missing first to avoid extra redirects
          try {
            console.log("Checking phone verification status directly...");
            const phoneResponse = await fetch(`${API_BASE}/signup/${registrationToken}/phonev`);
            
            if (phoneResponse.ok) {
              const phoneData = await phoneResponse.json();
              console.log("Phone verification direct check:", phoneData);
              
              // If phone is missing, go directly to update phone
              if (phoneData.missingPhone === true) {
                console.log("Missing phone detected, going directly to update phone");
                navigate(`/signup/${registrationToken}/updatephone`, { replace: true });
                return;
              }
            }
          } catch (err) {
            console.error("Error checking phone status:", err);
            // Continue with normal flow if this check fails
          }
          
          // Proceed with registration state check
          fetch(`${API_BASE}/signup/${registrationToken}/check`)
            .then(response => {
              if (!response.ok) {
                throw new Error('Failed to check registration state');
              }
              return response.json();
            })
            .then(stateData => {
              console.log("Registration state after Google sign-in:", stateData);
              
              // Navigate based on registration state
              switch(stateData.state) {
                case 'InviteForm':
                  navigate(`/signup/${registrationToken}/invite`, { replace: true });
                  break;
                case 'MailVerification':
                  navigate(`/signup/${registrationToken}`, { replace: true });
                  break;
                case 'PhoneVerification':
                  navigate(`/signup/${registrationToken}/phoneverification`, { replace: true });
                  break;
                case 'Login':
                  navigate('/login', { replace: true });
                  break;
                default:
                  navigate(`/signup/${registrationToken}`, { replace: true });
              }
            })
            .catch(err => {
              console.error('Failed to check registration state:', err);
              // Fall back to basic redirect
              navigate(`/signup/${registrationToken}`, { replace: true });
            });
        } else {
          setGeneralError('Invalid response from server');
        }
      } else {
        setGeneralError(data.message || 'Google login failed. Please try again.');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setGeneralError('Connection error. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate])

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPassword = () => {
    navigate('/forgotpwd');
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: ''
    };
    let isValid = true;

    // First check if fields are empty
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6 || !/^[\x20-\x7E]+$/.test(formData.password)) {
      newErrors.password = 'Invalid password';
      isValid = false;
    }

    setErrors(newErrors);

    // Focus on the first field with an error
    if (!isValid) {
      if (newErrors.email) {
        document.getElementById('email').focus();
      } else if (newErrors.password) {
        document.getElementById('password').focus();
      }
    }

    return isValid;
  };

  return (
    <div className="w-full bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] p-6">
      <h2 className="text-2xl font-bold mb-4 text-[var(--text-primary)]">Login</h2>
      {generalError && <p className="mb-3 text-red-500">{generalError}</p>}

      <form onSubmit={handleSubmit} noValidate autoComplete="on" className="space-y-4">
        <div>
          <label htmlFor="email" className="block mb-1 text-[var(--text-primary)]">Email</label>
          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className={`w-full p-2 pl-10 pr-3 border rounded bg-[var(--card-bg)] focus:outline-none ${
                errors.email 
                  ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                  : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
              } disabled:opacity-50`}
              disabled={loading}
            />
            <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
          {errors.email && (
            <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block mb-1 text-[var(--text-primary)]">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              autoComplete="current-password"
              className={`w-full p-2 pl-10 pr-9 border rounded bg-[var(--card-bg)] focus:outline-none ${
                errors.password 
                  ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                  : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
              } disabled:opacity-50`}
            />
            <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-2 flex items-center"
              tabIndex={-1}
              disabled={loading}
            >
              {showPassword ? <EyeOff className="w-5 h-5 text-[var(--text-primary)]" /> : <Eye className="w-5 h-5 text-[var(--text-primary)]" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-red-500 text-sm">{errors.password}</p>
          )}
        </div>

        <div className="flex flex-col space-y-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--hover-dark)] focus-visible:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
          >
            {loading ? (
              <>
                <LoadingSpinner />
                Logging in...
              </>
            ) : (
              <>
                Login
              </>
            )}
          </button>
          
          <div className="mt-4 text-center">
            <button 
              type="button"
              onClick={handleForgotPassword}
              className="text-[var(--accent-primary)] hover:text-white active:text-white focus-visible:text-white transition-colors duration-200"
            >
              Forgot your password?
            </button>
          </div>
        </div>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[var(--border-color)]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[var(--card-bg)] text-[var(--text-secondary)]">Or</span>
          </div>
        </div>

        <div id="google_btn_container">
          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={loading || googleLoading}
            className="w-full flex justify-center items-center gap-2 px-4 py-2 font-medium border border-[var(--border-color)] rounded bg-[var(--card-bg)] hover:bg-[var(--hover-light)] active:bg-[var(--hover-light)] focus-visible:bg-[var(--hover-light)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50"
          >
            <GoogleIcon />
            <span className="text-[var(--text-primary)]">Continue with Google</span>
          </button>
        </div>
      </form>
    </div>
  )
}
